import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpService } from '@core/services';
import { AuthenticationService } from '@core/services';
import { map } from 'rxjs/operators';
import { OpportunityDetail } from '../../../core/interface/opportunity-card-detail';

export type CrmProvider = 'salesforce' | 'zoho' | 'hubspot' | 'pipedrive';

export interface CrmRecordSearchItem {
  id: string;
  name: string;
  type: string;
}

interface CrmRecordSearchResponse {
  success?: boolean;
  count?: number;
  data?: CrmRecordSearchItem[];
}

export interface CrmRecordMapping {
  crm_record_id: string;
  crm_object_type: string;
  provider: string;
  crm_object_name: string;
}

interface CrmMapResponse {
  success?: boolean;
  message?: string;
  data?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OpportunityCardDetailService {
  constructor(
    private readonly _httpClient: HttpService,
    private readonly _authService: AuthenticationService
  ) { }

  getOpportunityDetail(
    opportunityId: any,
    createdAt: string
  ): Observable<OpportunityDetail> {
    return this._httpClient.post(
      'rest/v1/rpc/get_opportunity_questions_answers',
      {
        input_opportunity_id: opportunityId,
        input_updated_at: createdAt,
      }
    );
  }

  getOpportunityDates(input_opportunity_id: string): Observable<any> {
    return this._httpClient.post(
      'rest/v1/rpc/get_meeting_created_at', {
      input_opportunity_id
    });
  }

  /**
   * Fetch summary, extended summary and next steps in a single request.
   * They all live on the same post_call_processed row for a meeting, so there's
   * no reason to make three separate calls.
   */
  getPostCallProcessed(meeting_bot_id: string): Observable<any> {
    return this._httpClient.get(
      `rest/v1/post_call_processed?select=summary,extended_summary,next_steps&meeting_bot_id=eq.${meeting_bot_id}`);
  }

  getTranscript(input_meeting_bot_id: string): Observable<any> {
    return this._httpClient.post(
      `rest/v1/rpc/get_merged_transcripts_for_opportunity`, {
      input_meeting_bot_id
    });
  }

  getMeetingRecording(meeting_bot_id: string): Observable<any> {
    return this._httpClient.post('functions/v1/get-bot-data', {
      meeting_bot_id: meeting_bot_id
    });
  }

  updateAnswer(
    questionId: string,
    meetingBotId: string,
    answer: string
  ): Observable<any> {
    const url = `rest/v1/answers?question_id=eq.${questionId}&meeting_bot_id=eq.${meetingBotId}`;
    return this._httpClient.patch(url, { answer });
  }

  getCurrentCrmMapping(opportunityId: string, provider: CrmProvider): Observable<CrmRecordMapping | null> {
    return this._httpClient
      .get(
        `rest/v1/crm_record_mappings?select=crm_record_id,crm_object_name,crm_object_type,provider&opportunity_id=eq.${opportunityId}&provider=eq.${provider}&limit=1`,
        undefined,
        true
      )
      .pipe(
        map((response: CrmRecordMapping[] | CrmRecordMapping | null) => {
          if (!response) {
            return null;
          }

          const mapping = Array.isArray(response) ? response[0] : response;
          if (!mapping?.crm_record_id) {
            return null;
          }

          return mapping;
        })
      );
  }

  async searchCrmRecords(provider: CrmProvider, search: string): Promise<CrmRecordSearchItem[]> {
    const query = encodeURIComponent(search.trim());
    if (!query) {
      return [];
    }

    const response = await this._callCrmApi<CrmRecordSearchResponse>(
      `https://crm.convverse.ai/crm/${provider}/records/list?search=${query}&q=${query}`,
      'GET'
    );

    if (!response?.success) {
      throw new Error('Failed to search CRM records');
    }

    const records = Array.isArray(response.data) ? response.data : [];
    return records.filter(record => !!record?.id && !!record?.name);
  }

  async mapCrmRecord(provider: CrmProvider, opportunityId: string, crmRecordId: string, crmRecordName: string, crmType: string): Promise<CrmMapResponse> {
    const response = await this._callCrmApi<CrmMapResponse>(
      `https://crm.convverse.ai/crm/${provider}/records/map`,
      'POST',
      {
        opportunity_id: opportunityId,
        crm_record_id: crmRecordId,
        crm_object_type: crmType,
        crm_object_name: crmRecordName
      }
    );

    if (!response?.success) {
      throw new Error(response?.message || 'Failed to map CRM record');
    }

    return response;
  }

  async pushMeetingSummaryToCrm(provider: CrmProvider, opportunityId: string, botId: string): Promise<CrmMapResponse> {
    const response = await this._callCrmApi<CrmMapResponse>(
      `https://crm.convverse.ai/crm/${provider}/sync/push`,
      'POST',
      {
        opportunity_id: opportunityId,
        botid: botId,
      }
    );

    if (!response?.success) {
      throw new Error(response?.message || 'Failed to push meeting summary to CRM');
    }

    return response;
  }

  private async _callCrmApi<T>(
    url: string,
    method: 'GET' | 'POST',
    body?: unknown,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    const authToken = this._authService.getAuthToken;
    if (!authToken) {
      throw new Error('No active session');
    }

    const headers: Record<string, string> = {
      Authorization: authToken,
      Accept: 'application/json',
      ...(extraHeaders || {}),
    };

    const options: RequestInit = {
      method,
      headers,
    };

    if (method === 'POST') {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body ?? {});
    }

    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`CRM API request failed with status ${response.status}`);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new Error('CRM API returned non-JSON response');
    }

    return (await response.json()) as T;
  }
}
